import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api-client';
import { useSession } from '@/contexts/SessionContext';

export type MatchStatus = 'upcoming' | 'live' | 'half_time' | 'complete' | 'postponed';

export type ActiveMatch = {
  id: string;
  roundId: string;
  homeTeamCode: string | null;
  awayTeamCode: string | null;
  homeScore: number;
  awayScore: number;
  status: MatchStatus;
  kickoffAt: string | null;
  minute: number | null;
};

export type MatchesResponse = { matches: ActiveMatch[] };

export const MATCHES_QUERY_KEY = ['squads', 'matches'] as const;

export function useActiveMatches() {
  const session = useSession();

  return useQuery<MatchesResponse>({
    queryKey: MATCHES_QUERY_KEY,
    queryFn: async () => (await apiClient.get('/api/squads/matches')) as unknown as MatchesResponse,
    enabled: session.status === 'ready',
    refetchInterval: query => {
      const matches = query.state.data?.matches ?? [];
      if (matches.some(m => m.status === 'live' || m.status === 'half_time')) return 12_000;
      const nearKickoff = matches.some(m => {
        if (m.status !== 'upcoming' || !m.kickoffAt) return false;
        return new Date(m.kickoffAt).getTime() - Date.now() < 2 * 60 * 1000;
      });
      return nearKickoff ? 12_000 : 60_000;
    },
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}
